/**
 * EducationalLightbox — reusable modal that teaches first-time RV buyers
 * about the choices they're being asked to make on the lead form.
 *
 * Usage:
 *   <EducationalLightbox topic="rv_types" open={open} onClose={...} isDark={isDark} />
 *
 * Topics:
 *   - rv_types           full guide to Class A/B/C, TT, 5W, TH, Pop-up, Truck Camper
 *   - floor_plans        bunkhouse, rear kitchen, rear living, etc.
 *   - tow_vehicle        picking the right vehicle to tow
 *   - tow_weight         GVWR / dry weight / tongue weight explained
 */
import React, { useEffect } from 'react';
import { X, Info, Lightbulb, Truck, Home, Car, Weight } from 'lucide-react';

// ---------- Content library ----------
const CONTENT = {
  rv_types: {
    title: 'RV Types 101',
    subtitle: 'Every RV falls into one of these categories. Here\'s what each is best for.',
    Icon: Truck,
    accent: 'amber',
    sections: [
      {
        emoji: '🚐',
        name: 'Travel Trailer',
        tagline: 'Most popular. Most flexible.',
        bullets: [
          'Towed behind a truck or SUV with a bumper hitch.',
          'Range from 12-ft teardrops to 35-ft family bunkhouses.',
          'Great for weekenders — unhitch at camp and keep your tow vehicle.',
        ],
        need: 'Tow vehicle rated for the trailer\'s loaded weight (usually 5,000-9,500 lb).',
      },
      {
        emoji: '🏠',
        name: 'Fifth Wheel',
        tagline: 'Spacious. Truck-required.',
        bullets: [
          'Uses a special hitch in the bed of a pickup truck.',
          'Tallest ceilings and multi-level floor plans in the towable world.',
          'Ideal for long trips, full-timing, and families.',
        ],
        need: '3/4-ton or 1-ton pickup truck with fifth-wheel hitch installed.',
      },
      {
        emoji: '🚌',
        name: 'Class A Motorhome',
        tagline: 'Bus-sized luxury.',
        bullets: [
          'Largest motorhomes — built on a heavy-duty chassis.',
          'Drive it like a bus; no separate tow vehicle needed.',
          'Slide-outs create massive living rooms and bedrooms.',
        ],
        need: 'No tow vehicle required. You can flat-tow a car behind for local trips.',
      },
      {
        emoji: '🚐',
        name: 'Class C Motorhome',
        tagline: 'The friendly middle ground.',
        bullets: [
          'Built on a truck/van chassis with an over-cab sleeping area.',
          'Easier to drive than a Class A, more space than a van.',
          'Best all-rounder for families new to motorhomes.',
        ],
        need: 'No tow vehicle required.',
      },
      {
        emoji: '🚙',
        name: 'Camper Van (Class B)',
        tagline: 'Compact. Nimble. Stealthy.',
        bullets: [
          'A van conversion you can park anywhere a large SUV fits.',
          'Perfect for couples, remote workers, and city-to-trail travel.',
          'Small bathrooms, small kitchens — but everything you need.',
        ],
        need: 'No tow vehicle required. Great daily driver too.',
      },
      {
        emoji: '🏍️',
        name: 'Toy Hauler',
        tagline: 'Garage on wheels.',
        bullets: [
          'Rear "garage" for ATVs, motorcycles, kayaks, or bikes.',
          'Ramp door doubles as an outdoor deck at camp.',
          'Available as travel trailers, fifth wheels, or motorhomes.',
        ],
        need: 'Similar to a travel trailer or fifth wheel of comparable length.',
      },
      {
        emoji: '⛺',
        name: 'Pop-Up Camper',
        tagline: 'Affordable. Lightweight.',
        bullets: [
          'Folds down flat for towing, pops up into a canvas-walled camper.',
          'Tows behind almost any SUV or minivan.',
          'Basic amenities — sleeping, small dinette, minimal kitchen.',
        ],
        need: 'Light-duty SUV or minivan (usually under 3,500 lb loaded).',
      },
      {
        emoji: '🛻',
        name: 'Truck Camper',
        tagline: 'Rides in your truck bed.',
        bullets: [
          'Slides into the bed of a pickup truck — nothing to tow.',
          'Great for off-road adventures and tight campsites.',
          'Payload capacity is critical (not tow rating).',
        ],
        need: 'A pickup with matching payload capacity (check the yellow sticker in your door jamb).',
      },
    ],
    tip: 'Not sure? Pick the style that fits your camping trips today — you can always upgrade in a few years.',
  },

  floor_plans: {
    title: 'Floor Plan Layouts',
    subtitle: 'The interior layout matters more than the exterior looks. Here\'s what each layout means.',
    Icon: Home,
    accent: 'rose',
    sections: [
      {
        emoji: '🛏️',
        name: 'Bunkhouse',
        tagline: 'Best for families with kids.',
        bullets: [
          'Dedicated kids\' room with stacked bunk beds.',
          'Usually located in the rear or in an opposite-side slide-out.',
          'Some models fit 4-6 kids; look for privacy curtains and reading lights.',
        ],
      },
      {
        emoji: '🍳',
        name: 'Rear Kitchen',
        tagline: 'Best for cooking-focused travelers.',
        bullets: [
          'Full-size kitchen along the back wall, often with a huge window.',
          'Massive counter space and residential-style appliances.',
          'Bedroom moves to the front.',
        ],
      },
      {
        emoji: '🛋️',
        name: 'Rear Living',
        tagline: 'Best for couples who entertain.',
        bullets: [
          'Big rear-wall windows and a wraparound sofa.',
          'Feels more like a lounge than a hallway.',
          'Perfect for game nights and full hookup parks.',
        ],
      },
      {
        emoji: '🛌',
        name: 'Front Bedroom',
        tagline: 'Best for privacy.',
        bullets: [
          'Master bedroom up front — separated from the living area.',
          'Common in fifth wheels (loft-style master over the truck bed).',
          'Quiet, private, and often includes his-and-hers closets.',
        ],
      },
      {
        emoji: '🏍️',
        name: 'Toy Hauler',
        tagline: 'Best for gear haulers.',
        bullets: [
          'Rear "garage" holds motorcycles, ATVs, or a home gym.',
          'Ramp door often converts to a deck patio.',
          'Living area still has kitchen, bath, and beds.',
        ],
      },
      {
        emoji: '🛏️',
        name: 'Murphy Bed',
        tagline: 'Best for tight spaces.',
        bullets: [
          'Bed folds up into the wall during the day.',
          'Reveals a full living area — a lifesaver in short trailers.',
          'Common in camper vans and small trailers.',
        ],
      },
      {
        emoji: '🛁',
        name: 'Couples / Rear Bath',
        tagline: 'Best for two adults, no kids.',
        bullets: [
          'Master suite and full bath dominate the layout.',
          'Big shower, dual sinks, walk-around bed common.',
          'Feels like an efficiency apartment.',
        ],
      },
    ],
    tip: 'You can multi-select — if you\'d be happy with a bunkhouse OR a rear kitchen, pick both.',
  },

  tow_vehicle: {
    title: 'Tow Vehicle Basics',
    subtitle: 'The #1 mistake first-time buyers make is buying an RV their vehicle can\'t safely pull.',
    Icon: Car,
    accent: 'blue',
    sections: [
      {
        emoji: '🚗',
        name: 'Small SUV / Minivan',
        tagline: '~3,500 lb tow rating',
        bullets: [
          'Toyota RAV4, Honda Pilot, Chrysler Pacifica, etc.',
          'Best for pop-ups, teardrops, and light-weight travel trailers under 20 ft.',
        ],
      },
      {
        emoji: '🚙',
        name: 'Mid-Size SUV / Half-Ton Truck',
        tagline: '5,000-9,500 lb tow rating',
        bullets: [
          'Ford F-150, Ram 1500, Chevy Silverado 1500, Toyota Sequoia.',
          'Handles most 20-28 ft travel trailers comfortably.',
          'Verify the exact tow package on YOUR truck — trims matter a lot.',
        ],
      },
      {
        emoji: '🛻',
        name: '3/4-Ton Pickup',
        tagline: '12,000-17,000 lb tow rating',
        bullets: [
          'Ford F-250, Ram 2500, Chevy Silverado 2500HD.',
          'Required for most fifth wheels and larger travel trailers.',
          'Best all-around family RV tow vehicle.',
        ],
      },
      {
        emoji: '🚛',
        name: '1-Ton Dually',
        tagline: '18,000-30,000 lb tow rating',
        bullets: [
          'F-350 Dually, Ram 3500, Silverado 3500HD.',
          'Serious full-timers and big fifth wheels.',
          'Overkill for most weekend RVers.',
        ],
      },
    ],
    tip: 'Rule of thumb: never load more than 80% of your vehicle\'s max tow rating. Leave headroom for hills, wind, gear, and fuel.',
  },

  tow_weight: {
    title: 'Understanding Tow Weight',
    subtitle: 'These four numbers decide whether your RV is safe to tow.',
    Icon: Weight,
    accent: 'orange',
    sections: [
      {
        emoji: '⚖️',
        name: 'Dry Weight (UVW)',
        tagline: 'What the RV weighs empty from the factory.',
        bullets: [
          'Also called Unloaded Vehicle Weight.',
          'This is what dealers advertise — but it\'s NOT what you\'ll actually tow.',
        ],
      },
      {
        emoji: '📦',
        name: 'GVWR (Gross Vehicle Weight Rating)',
        tagline: 'The max the RV can weigh once loaded.',
        bullets: [
          'Dry weight + water + propane + gear + food + you.',
          'This is the number you should compare to your truck\'s tow rating.',
          'Real-world loaded weight is often 1,000-2,500 lb over dry weight.',
        ],
      },
      {
        emoji: '🔩',
        name: 'Tongue Weight',
        tagline: 'The weight pressing down on your hitch ball.',
        bullets: [
          'Typically 10-15% of the trailer\'s loaded weight.',
          'Must stay under your hitch receiver\'s rating.',
          'Too little tongue weight = trailer sway. Too much = your rear axle groans.',
        ],
      },
      {
        emoji: '🛑',
        name: 'Payload Capacity',
        tagline: 'How much stuff YOUR truck can carry.',
        bullets: [
          'Different from tow rating — this is passengers + gear + hitch weight.',
          'Check the yellow sticker on the driver\'s door jamb.',
          'A truck rated to tow 12,000 lb but only carry 1,500 lb of payload is limited by payload.',
        ],
      },
    ],
    tip: 'When in doubt: match your RV\'s GVWR to no more than 80% of your truck\'s tow rating, AND keep tongue weight under 80% of payload capacity.',
  },

  budget_financing: {
    title: 'Budget & Financing 101',
    subtitle: 'What "budget" actually costs — and how RV financing really works.',
    Icon: Weight,
    accent: 'green',
    sections: [
      {
        emoji: '💰',
        name: 'All-in Budget',
        tagline: 'Sticker price is only about 80% of what you\'ll actually pay.',
        bullets: [
          'Add ~7-9% for tax, title, doc fees, and registration.',
          'First-year insurance runs $800-$2,000 depending on class and state.',
          'Extended warranties, hitch installs, and lot prep are extra — negotiate them in.',
        ],
      },
      {
        emoji: '🏦',
        name: 'RV Loan Terms',
        tagline: 'RV loans stretch longer than car loans — and cost more in interest.',
        bullets: [
          'Common terms: 10, 12, 15, even 20 years for units over ~$50k.',
          'Rates typically 1-2% above auto rates; 720+ credit unlocks the best pricing.',
          'A 240-month loan cuts your payment by half but roughly doubles total interest.',
        ],
      },
      {
        emoji: '💳',
        name: 'Down Payment',
        tagline: 'Lenders want skin in the game — RVs depreciate fast.',
        bullets: [
          '10-20% down is standard for new; 20%+ typical for used.',
          'A trade-in counts toward down payment.',
          'Larger down = smaller payment AND less risk of going upside-down as it depreciates.',
        ],
      },
      {
        emoji: '📊',
        name: 'Pre-approval',
        tagline: 'Know your true buying power before you fall in love.',
        bullets: [
          'Get pre-approved with your bank or credit union first — dealer financing is competition, not your only option.',
          'Pre-approval locks a rate for 30-60 days and lets you negotiate as a cash buyer.',
          'RV Match can match you with lenders who specialize in your credit range.',
        ],
      },
    ],
    tip: 'Rule of thumb: your total monthly RV cost (payment + insurance + storage + fuel budget) should stay under 15% of take-home pay.',
  },
};

const ACCENT_STYLES = {
  amber: { ring: 'ring-amber-400', bg: 'bg-amber-400/10', text: 'text-amber-400', border: 'border-amber-400/30' },
  rose:  { ring: 'ring-rose-400',  bg: 'bg-rose-400/10',  text: 'text-rose-400',  border: 'border-rose-400/30' },
  blue:  { ring: 'ring-sky-400',   bg: 'bg-sky-400/10',   text: 'text-sky-400',   border: 'border-sky-400/30' },
  orange:{ ring: 'ring-orange-400',bg: 'bg-orange-400/10',text: 'text-orange-400',border: 'border-orange-400/30' },
  green: { ring: 'ring-emerald-400',bg: 'bg-emerald-400/10',text: 'text-emerald-400',border: 'border-emerald-400/30' },
};

const EducationalLightbox = ({ topic, open, onClose, isDark = true }) => {
  const data = CONTENT[topic];

  // Prevent background scroll while open
  useEffect(() => {
    if (!open) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = original; };
  }, [open]);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !data) return null;
  const { title, subtitle, Icon, accent, sections, tip } = data;
  const styles = ACCENT_STYLES[accent] || ACCENT_STYLES.amber;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4"
      data-testid={`edu-lightbox-${topic}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby={`edu-title-${topic}`}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
        data-testid={`edu-lightbox-backdrop-${topic}`}
      />

      {/* Panel — full-screen on mobile, centered card on desktop */}
      <div
        className={`
          relative w-full sm:max-w-3xl sm:rounded-2xl overflow-hidden
          ${isDark ? 'bg-[#0b1220] text-white' : 'bg-white text-gray-900'}
          shadow-2xl border ${isDark ? 'border-white/10' : 'border-gray-200'}
          max-h-[100vh] sm:max-h-[90vh] flex flex-col
        `}
      >
        {/* Header */}
        <div className={`sticky top-0 z-10 px-5 sm:px-8 py-4 sm:py-5 flex items-start gap-3 ${isDark ? 'bg-[#0b1220]/95' : 'bg-white/95'} backdrop-blur border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
          <div className={`shrink-0 w-11 h-11 rounded-xl flex items-center justify-center ${styles.bg} ${styles.border} border`}>
            <Icon className={`w-5 h-5 ${styles.text}`} />
          </div>
          <div className="min-w-0 flex-1">
            <h2
              id={`edu-title-${topic}`}
              className="text-lg sm:text-2xl font-bold leading-tight tracking-tight"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              {title}
            </h2>
            <p className={`text-xs sm:text-sm mt-1 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>{subtitle}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={`shrink-0 -mr-1 p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
            aria-label="Close"
            data-testid={`edu-lightbox-close-${topic}`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto px-5 sm:px-8 py-5 sm:py-6 space-y-4">
          {sections.map((s, idx) => (
            <div
              key={s.name}
              className={`rounded-xl border p-4 sm:p-5 ${isDark ? 'border-white/10 bg-white/[0.03]' : 'border-gray-200 bg-gray-50'}`}
              data-testid={`edu-section-${topic}-${idx}`}
            >
              <div className="flex items-start gap-3">
                <div className="text-3xl leading-none shrink-0">{s.emoji}</div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <h3 className={`font-bold text-base sm:text-lg ${styles.text}`}>{s.name}</h3>
                    {s.tagline ? (
                      <span className={`text-xs sm:text-sm ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                        {s.tagline}
                      </span>
                    ) : null}
                  </div>
                  <ul className={`mt-2 space-y-1.5 text-sm ${isDark ? 'text-white/85' : 'text-gray-700'}`}>
                    {s.bullets.map((b, i) => (
                      <li key={i} className="flex gap-2">
                        <span className={`shrink-0 mt-1 w-1.5 h-1.5 rounded-full ${styles.bg} border ${styles.border}`} aria-hidden />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  {s.need ? (
                    <div className={`mt-3 text-xs sm:text-sm flex gap-2 items-start ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                      <Info className={`shrink-0 w-4 h-4 mt-0.5 ${styles.text}`} />
                      <span><strong>What you need:</strong> {s.need}</span>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          ))}

          {tip ? (
            <div className={`rounded-xl border p-4 sm:p-5 ${styles.bg} ${styles.border}`}>
              <div className="flex gap-3 items-start">
                <Lightbulb className={`shrink-0 w-5 h-5 ${styles.text}`} />
                <p className={`text-sm sm:text-base ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  <strong className={styles.text}>Pro tip · </strong>{tip}
                </p>
              </div>
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className={`px-5 sm:px-8 py-3 sm:py-4 border-t ${isDark ? 'border-white/10 bg-[#0b1220]' : 'border-gray-200 bg-white'}`}>
          <button
            type="button"
            onClick={onClose}
            className={`w-full sm:w-auto sm:ml-auto sm:flex block px-6 py-3 rounded-lg font-bold uppercase tracking-wide transition-colors ${styles.bg} ${styles.text} border ${styles.border} hover:brightness-110`}
            data-testid={`edu-lightbox-done-${topic}`}
          >
            Got it — take me back
          </button>
        </div>
      </div>
    </div>
  );
};

// Small info-icon button that opens the lightbox next to a section header.
export const EduInfoButton = ({ topic, onOpen, isDark = true, label = 'What\'s this?' }) => (
  <button
    type="button"
    onClick={() => onOpen(topic)}
    className={`
      inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-semibold transition-colors
      ${isDark
        ? 'bg-white/10 text-white/90 border border-white/15 hover:bg-white/15'
        : 'bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100'}
    `}
    data-testid={`edu-info-btn-${topic}`}
    aria-label={`Learn about ${topic.replace('_', ' ')}`}
  >
    <Info className="w-3.5 h-3.5" />
    <span>{label}</span>
  </button>
);

export default EducationalLightbox;
